import React from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Logout, Person, Key, SupportAgent } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';

interface PublicLayoutProps {
  children: React.ReactNode;
}

export default function PublicLayout({ children }: PublicLayoutProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { user, logout, getUser, token, isAuthenticated } = useAuthStore();

  // Fetch user data when component mounts
  React.useEffect(() => {
    // Always try to fetch user data on mount
    const fetchUserData = async () => {
      try {
        await getUser();
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, [getUser, token, isAuthenticated]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Electronic BIRA
          </Typography>
          
          {/* User name and icon */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {user && (
              <Box sx={{ mr: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography 
                  variant="body1" 
                  color="white"
                  sx={{ 
                    fontWeight: 'medium',
                    lineHeight: 1.2
                  }}
                >
                  {user.full_name || 'User'}
                </Typography>
                <Typography 
                  variant="body2"
                  sx={{ 
                    opacity: 0.8,
                    lineHeight: 0.75
                  }}
                >
                  {user.email || ''}
                </Typography>
              </Box>
            )}
            
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Person />
            </IconButton>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {user ? (
                <MenuItem disabled>
                  {user.full_name || 'User'} ({user.email || ''})
                </MenuItem>
              ) : (
                <MenuItem disabled>Loading user data...</MenuItem>
              )}
              <Divider />
              <MenuItem onClick={() => { handleClose(); navigate('/ticketing'); }}>
                <ListItemIcon>
                  <SupportAgent fontSize="small" />
                </ListItemIcon>
                Tickets
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); navigate('/change-password'); }}>
                <ListItemIcon>
                  <Key fontSize="small" />
                </ListItemIcon>
                Change Password
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: '100%',
          mt: '64px' // Add margin top to account for AppBar height
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
